import { useEffect, useState, useMemo, useCallback } from "react";
import Chart, { ChartItem } from "chart.js/auto";
import { Project } from "../../features/projects/Project";
import { Milestone } from "../../features/projects/Milestone";
import {
  Box,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { fetchAllAccessibleProjects } from "../../features/projects/projectApi";

const ProjectProgress = () => {
  const defaultProject = useMemo(
    () => ({
      id: 0,
      name: "",
      milestones: [],
    }),
    []
  );

  const defaultMilestone = useMemo(
    () => ({
      id: 0,
      name: "",
      estimateChartLines: {
        totalEstimate: [],
        actualBurnUp: [],
        estimateBurnUp: [],
        estimatedTotalHoursNeeded: [],
        totalTaskEstimates: [],
        totalOfEstimatedCompleted: [],
        totalTasks: [],
      },
      gitLabCreateDate: "",
    }),
    []
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const [previousMilestone, setPreviousMilestone] = useState<Milestone | null>(
    null
  );
  const projectId = searchParams.get("projectId");
  const milestoneId = searchParams.get("milestoneId");
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] =
    useState<Project>(defaultProject);
  const [selectedMilestone, setSelectedMilestone] =
    useState<Milestone>(defaultMilestone);
  const [chart, setChart] = useState<Chart | null>(null);

  const setProjectAndMilestone = useCallback(
    (project: Project, milestone: Milestone) => {
      setSelectedProject(project);
      setSelectedMilestone(milestone);
      setPreviousMilestone(milestone);
      setSearchParams({
        projectId: project.id.toString(),
        milestoneId: milestone.id.toString(),
      });
    },
    [
      setSelectedProject,
      setSelectedMilestone,
      setPreviousMilestone,
      setSearchParams,
    ]
  );

  const resetSelection = useCallback(() => {
    setSelectedProject(defaultProject);
    setSelectedMilestone(defaultMilestone);
  }, [
    setSelectedProject,
    setSelectedMilestone,
    defaultMilestone,
    defaultProject,
  ]);

  const findMilestone = useCallback(
    (project: Project) => {
      const milestones = project.milestones || [];
      return milestoneId
        ? milestones.find((m) => m.id === parseInt(milestoneId)) ||
            milestones[0]
        : milestones[0];
    },
    [milestoneId]
  );

  const loadProjects = useCallback(async () => {
    try {
      if (projects.length > 0) return;

      const fetchedProjects = await fetchAllAccessibleProjects();
      if (fetchedProjects.length === 0) {
        resetSelection();
        return;
      }

      setProjects(fetchedProjects);

      const currentProject = projectId
        ? fetchedProjects.find((p) => p.id === parseInt(projectId)) ||
          fetchedProjects[0]
        : fetchedProjects[0];

      if (currentProject?.milestones?.length) {
        const currentMilestone = findMilestone(currentProject);
        if (currentMilestone) {
          setProjectAndMilestone(currentProject, currentMilestone);
        }
      } else {
        resetSelection();
      }
    } catch (error) {
      resetSelection();
    }
  }, [
    projects,
    projectId,
    setProjectAndMilestone,
    resetSelection,
    findMilestone,
  ]);

  useEffect(() => {
    if (projects.length === 0 || projectId || milestoneId) {
      loadProjects();
    }
  }, [projectId, milestoneId, loadProjects, projects.length]);

  useEffect(() => {
    if (projects.length > 0 && projectId && milestoneId) {
      const currentProject =
        projects.find((p) => p.id === parseInt(projectId)) || projects[0];
      if (currentProject?.milestones?.length) {
        const currentMilestone = findMilestone(currentProject);
        if (currentMilestone) {
          setProjectAndMilestone(currentProject, currentMilestone);
        }
      } else {
        resetSelection();
      }
    }
  }, [
    projects,
    projectId,
    milestoneId,
    findMilestone,
    resetSelection,
    setProjectAndMilestone,
  ]);

  useEffect(() => {
    if (!selectedMilestone?.estimateChartLines) {
      return () => {};
    }

    const chartItem = document.getElementById("chart-metrics") as ChartItem;

    if (chart != null) {
      chart.data.labels =
        selectedMilestone?.estimateChartLines?.totalEstimate?.map((item) =>
          new Date(item.date).toLocaleDateString()
        ) || [];
      chart.data.datasets = [
        {
          label: "Total Estimate",
          tension: 0.4,
          data:
            selectedMilestone?.estimateChartLines?.totalEstimate?.map(
              (item) => item.value
            ) || [],
          borderColor: "rgba(75, 192, 192, 1)",
          fill: false,
        },
        {
          label: "Actual Burnup",
          tension: 0.4,
          data:
            selectedMilestone?.estimateChartLines?.actualBurnUp?.map(
              (item) => item.value
            ) || [],
          borderColor: "rgba(255, 99, 132, 1)",
          fill: false,
        },
        {
          label: "Estimate Burnup",
          tension: 0.4,
          data:
            selectedMilestone?.estimateChartLines?.estimateBurnUp?.map(
              (item) => item.value
            ) || [],
          borderColor: "rgba(255, 159, 64, 1)",
          fill: false,
        },
        {
          label: "Estimated Total Hours Needed",
          tension: 0.4,
          data:
            selectedMilestone?.estimateChartLines?.estimatedTotalHoursNeeded?.map(
              (item) => item.value
            ) || [],
          borderColor: "rgba(153, 102, 255, 1)",
          fill: false,
        },
        {
          label: "Total Task Estimate",
          tension: 0.4,
          data:
            selectedMilestone?.estimateChartLines?.totalTaskEstimates?.map(
              (item) => item.value
            ) || [],
          borderColor: "rgba(54, 162, 235, 1)",
          fill: false,
        },
        {
          label: "Total Of Estimated Completed",
          tension: 0.4,
          data:
            selectedMilestone?.estimateChartLines?.totalOfEstimatedCompleted?.map(
              (item) => item.value
            ) || [],
          borderColor: "rgba(255, 105, 180, 1)",
          fill: false,
        },
        {
          label: "Total Tasks",
          tension: 0.4,
          data:
            selectedMilestone?.estimateChartLines?.totalTasks?.map(
              (item) => item.value
            ) || [],
          borderColor: "rgba(201, 203, 207, 1)",
          fill: false,
        },
      ];
      chart.update();
    } else {
      const newChart = new Chart(chartItem, {
        type: "line",
        data: {
          labels: selectedMilestone.estimateChartLines.totalEstimate.map(
            (item) => new Date(item.date).toLocaleDateString()
          ),
          datasets: [
            {
              label: "Total Estimate",
              tension: 0.4,
              data: selectedMilestone.estimateChartLines.totalEstimate.map(
                (item) => item.value
              ),
              borderColor: "rgba(75, 192, 192, 1)",
              fill: false,
            },
            {
              label: "Actual Burnup",
              tension: 0.4,
              data: selectedMilestone.estimateChartLines.actualBurnUp.map(
                (item) => item.value
              ),
              borderColor: "rgba(255, 99, 132, 1)",
              fill: false,
            },
            {
              label: "Estimate Burnup",
              tension: 0.4,
              data: selectedMilestone.estimateChartLines.estimateBurnUp.map(
                (item) => item.value
              ),
              borderColor: "rgba(255, 159, 64, 1)",
              fill: false,
            },
            {
              label: "Estimated Total Hours Needed",
              tension: 0.4,
              data: selectedMilestone.estimateChartLines.estimatedTotalHoursNeeded.map(
                (item) => item.value
              ),
              borderColor: "rgba(153, 102, 255, 1)",
              fill: false,
            },
            {
              label: "Total Task Estimate",
              tension: 0.4,
              data: selectedMilestone.estimateChartLines.totalTaskEstimates.map(
                (item) => item.value
              ),
              borderColor: "rgba(54, 162, 235, 1)",
              fill: false,
            },
            {
              label: "Total Of Estimated Completed",
              tension: 0.4,
              data: selectedMilestone.estimateChartLines.totalOfEstimatedCompleted.map(
                (item) => item.value
              ),
              borderColor: "rgba(255, 105, 180, 1)",
              fill: false,
            },
            {
              label: "Total Tasks",
              tension: 0.4,
              data: selectedMilestone.estimateChartLines.totalTasks.map(
                (item) => item.value
              ),
              borderColor: "rgba(201, 203, 207, 1)",
              fill: false,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
        },
      });
      setChart(newChart);
    }

    return () => {};
  }, [selectedMilestone, chart]);

  useEffect(() => {
    return () => {
      if (chart) {
        chart.destroy();
      }
    };
  }, [chart]);

  const handleProjectChange = (event: SelectChangeEvent<string>) => {
    const selectedProject = projects.find(
      (project) => project.name === event.target.value
    );

    if (
      typeof selectedProject === "undefined" ||
      typeof selectedProject.milestones?.length === "undefined"
    ) {
      return;
    }

    const milestone = previousMilestone
      ? findMilestoneByNameForProject(selectedProject, previousMilestone.name)
      : selectedProject.milestones[0];
    if (typeof milestone === "undefined") {
      return;
    }

    setSelectedProject(selectedProject);
    setSelectedMilestone(milestone);
    setPreviousMilestone(milestone);
    setSearchParams({
      projectId: selectedProject.id.toString(),
      milestoneId: milestone.id.toString(),
    });
  };

  const findMilestoneByNameForProject = (
    project: Project,
    milestoneName: string
  ): Milestone | undefined => {
    return project.milestones.find(
      (milestone) => milestone.name === milestoneName
    );
  };

  const handleMilestoneChange = (event: SelectChangeEvent<string>) => {
    const selectedMilestone = selectedProject.milestones.find(
      (milestone) => milestone.name === event.target.value
    );
    if (selectedMilestone) {
      setSelectedMilestone(selectedMilestone);
      setPreviousMilestone(selectedMilestone);
      setSearchParams({
        projectId: selectedProject.id.toString(),
        milestoneId: selectedMilestone.id.toString(),
      });
    }
  };

  const milestones = [...selectedProject.milestones].sort((a, b) => {
    const dateA = new Date(a.gitLabCreateDate);
    const dateB = new Date(b.gitLabCreateDate);
    return dateB.getTime() - dateA.getTime();
  });

  const ratio =
    (selectedMilestone?.estimateChartLines?.actualBurnUp?.[
      (selectedMilestone?.estimateChartLines?.actualBurnUp?.length || 1) - 1
    ]?.value || 0) /
    (selectedMilestone?.estimateChartLines?.totalTaskEstimates?.[
      (selectedMilestone?.estimateChartLines?.totalTaskEstimates?.length || 1) -
        1
    ]?.value || 1);

  const actualToTotalEstimateRatio =
    (selectedMilestone?.estimateChartLines?.actualBurnUp?.[
      (selectedMilestone?.estimateChartLines?.actualBurnUp?.length || 1) - 1
    ]?.value || 0) /
    (selectedMilestone?.estimateChartLines?.totalEstimate?.[
      (selectedMilestone?.estimateChartLines?.totalEstimate?.length || 1) - 1
    ]?.value || 1);

  const actualToEstimateBurnupRatio =
    (selectedMilestone?.estimateChartLines?.actualBurnUp?.[
      (selectedMilestone?.estimateChartLines?.actualBurnUp?.length || 1) - 1
    ]?.value || 0) /
    (selectedMilestone?.estimateChartLines?.estimateBurnUp?.[
      (selectedMilestone?.estimateChartLines?.estimateBurnUp?.length || 1) - 1
    ]?.value || 1);

  let color =
    (ratio <= 1 && "rgba(75, 192, 192, 1)") ||
    (ratio > 1 && ratio <= 1.2 && "rgba(255, 159, 64, 1)") ||
    "rgba(255, 99, 132, 1)";

  let actualToTotalEstimateColor =
    (actualToTotalEstimateRatio <= 1 && "rgba(75, 192, 192, 1)") ||
    (actualToTotalEstimateRatio > 1 &&
      actualToTotalEstimateRatio <= 1.2 &&
      "rgba(255, 159, 64, 1)") ||
    "rgba(255, 99, 132, 1)";

  let actualToEstimateBurnupColor =
    (actualToEstimateBurnupRatio <= 1 && "rgba(75, 192, 192, 1)") ||
    (actualToEstimateBurnupRatio > 1 &&
      actualToEstimateBurnupRatio <= 1.2 &&
      "rgba(255, 159, 64, 1)") ||
    "rgba(255, 99, 132, 1)";

  const lastKnownDate =
    selectedMilestone?.estimateChartLines?.totalEstimate?.[
      (selectedMilestone?.estimateChartLines?.totalEstimate?.length || 1) - 1
    ]?.date;

  const formattedLastKnownDate = lastKnownDate
    ? new Date(lastKnownDate).toLocaleString()
    : "No data available";

  return (
    <>
      <Container
        className="App"
        maxWidth="lg"
        sx={{
          px: 4,
        }}
      >
        <Typography
          variant="h4"
          component="h1"
          sx={{
            textAlign: "left",
            mt: 4,
          }}
        >
          Project progress
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "column", lg: "row" },
            gap: { xs: 1, md: 2, lg: 4 },

            position: "relative",
          }}
        >
          <Box
            sx={{
              flex: 2,
              pt: 2,
              width: "100%",
              height: { xs: "400px", sm: "450px", md: "450px" },
              position: "relative",
            }}
          >
            <Box
              component="canvas"
              id="chart-metrics"
              sx={{
                height: {
                  xs: "400px !important",
                  sm: "450px !important",
                  md: "450px !important",
                  lg: "unset !important",
                  xl: "unset !important",
                },
              }}
            />
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              mt: { xs: 0, md: 2, lg: 2 },
              width: 300,
              height: "max-content",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 3,
                flex: 1,
                pt: { xs: 1, md: 2, lg: 2 },
              }}
            >
              <FormControl>
                <InputLabel>Project</InputLabel>
                <Select
                  value={selectedProject.name}
                  label="Project"
                  onChange={handleProjectChange}
                  sx={{ minWidth: 250 }}
                >
                  {projects.map((project) => (
                    <MenuItem key={project.id} value={project.name}>
                      {project.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl>
                <InputLabel>Milestone</InputLabel>
                <Select
                  value={selectedMilestone.name}
                  label="Milestone"
                  onChange={handleMilestoneChange}
                  disabled={!selectedProject}
                  sx={{ minWidth: 250 }}
                >
                  {milestones.map((milestone) => (
                    <MenuItem key={milestone.id} value={milestone.name}>
                      {milestone.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}
            >
              <Typography
                variant="h6"
                sx={{ borderBottom: "1px solid #e0e0e0" }}
              >
                Latest Values
              </Typography>

              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography sx={{ color: "rgba(75, 192, 192, 1)" }}>
                  Total estimate:
                </Typography>
                <Typography>
                  {(
                    selectedMilestone?.estimateChartLines?.totalEstimate?.[
                      (selectedMilestone?.estimateChartLines?.totalEstimate
                        ?.length || 1) - 1
                    ]?.value || 0
                  ).toFixed(2)}
                  h
                </Typography>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography sx={{ color: "rgba(255, 99, 132, 1)" }}>
                  Actual burnup:
                </Typography>
                <Typography>
                  {(
                    selectedMilestone?.estimateChartLines?.actualBurnUp?.[
                      (selectedMilestone?.estimateChartLines?.actualBurnUp
                        ?.length || 1) - 1
                    ]?.value || 0
                  ).toFixed(2)}
                  h
                </Typography>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography sx={{ color: "rgba(255, 159, 64, 1)" }}>
                  Estimate burnup:
                </Typography>
                <Typography>
                  {(
                    selectedMilestone?.estimateChartLines?.estimateBurnUp?.[
                      (selectedMilestone?.estimateChartLines?.estimateBurnUp
                        ?.length || 1) - 1
                    ]?.value || 0
                  ).toFixed(2)}
                  h
                </Typography>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography sx={{ color: "rgba(153, 102, 255, 1)" }}>
                  Est. Total hours:
                </Typography>
                <Typography>
                  {(
                    selectedMilestone?.estimateChartLines
                      ?.estimatedTotalHoursNeeded?.[
                      (selectedMilestone?.estimateChartLines
                        ?.estimatedTotalHoursNeeded?.length || 1) - 1
                    ]?.value || 0
                  ).toFixed(2)}
                  h
                </Typography>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography sx={{ color: "rgba(54, 162, 235, 1)" }}>
                  Total Task Estimate:
                </Typography>
                <Typography>
                  {(
                    selectedMilestone?.estimateChartLines?.totalTaskEstimates?.[
                      (selectedMilestone?.estimateChartLines?.totalTaskEstimates
                        ?.length || 1) - 1
                    ]?.value || 0
                  ).toFixed(2)}
                  h
                </Typography>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography sx={{ color: "rgba(255, 105, 180, 1)" }}>
                  Total Of Estimated Completed:
                </Typography>
                <Typography>
                  {(
                    selectedMilestone?.estimateChartLines
                      ?.totalOfEstimatedCompleted?.[
                      (selectedMilestone?.estimateChartLines
                        ?.totalOfEstimatedCompleted?.length || 1) - 1
                    ]?.value || 0
                  ).toFixed(2)}
                  h
                </Typography>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography sx={{ color: "rgba(201, 203, 207, 1)" }}>
                  Total Tasks:
                </Typography>
                <Typography>
                  {(
                    selectedMilestone?.estimateChartLines?.totalTasks?.[
                      (selectedMilestone?.estimateChartLines?.totalTasks
                        ?.length || 1) - 1
                    ]?.value || 0
                  ).toFixed(0)}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography color="rgba(0, 0, 133, 1)">
                  Gain From Estimate:
                </Typography>
                <Typography
                  sx={{
                    color:
                      (selectedMilestone?.estimateChartLines?.estimateBurnUp?.[
                        (selectedMilestone?.estimateChartLines?.estimateBurnUp
                          ?.length || 1) - 1
                      ]?.value || 0) -
                        (selectedMilestone?.estimateChartLines?.actualBurnUp?.[
                          (selectedMilestone?.estimateChartLines?.actualBurnUp
                            ?.length || 1) - 1
                        ]?.value || 0) ===
                      0
                        ? "rgba(75, 192, 192, 1)"
                        : (selectedMilestone?.estimateChartLines
                            ?.estimateBurnUp?.[
                            (selectedMilestone?.estimateChartLines
                              ?.estimateBurnUp?.length || 1) - 1
                          ]?.value || 0) -
                            (selectedMilestone?.estimateChartLines
                              ?.actualBurnUp?.[
                              (selectedMilestone?.estimateChartLines
                                ?.actualBurnUp?.length || 1) - 1
                            ]?.value || 0) >
                          0
                        ? "rgba(75, 192, 192, 1)"
                        : "rgba(255, 99, 132, 1)",
                  }}
                >
                  {(
                    (selectedMilestone?.estimateChartLines?.estimateBurnUp?.[
                      (selectedMilestone?.estimateChartLines?.estimateBurnUp
                        ?.length || 1) - 1
                    ]?.value || 0) -
                    (selectedMilestone?.estimateChartLines?.actualBurnUp?.[
                      (selectedMilestone?.estimateChartLines?.actualBurnUp
                        ?.length || 1) - 1
                    ]?.value || 0)
                  ).toFixed(2) === "0.00"
                    ? "0"
                    : (
                        (selectedMilestone?.estimateChartLines
                          ?.estimateBurnUp?.[
                          (selectedMilestone?.estimateChartLines?.estimateBurnUp
                            ?.length || 1) - 1
                        ]?.value || 0) -
                        (selectedMilestone?.estimateChartLines?.actualBurnUp?.[
                          (selectedMilestone?.estimateChartLines?.actualBurnUp
                            ?.length || 1) - 1
                        ]?.value || 0)
                      ).toFixed(2)}
                  h
                </Typography>
              </Box>

              <Typography
                variant="h6"
                sx={{
                  borderBottom: "1px solid #e0e0e0",
                }}
              >
                Ratios
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography>Actual burnup to Total task estimate:</Typography>
                  <Typography
                    sx={{
                      color: color,
                      fontWeight: "medium",
                    }}
                  >
                    {ratio.toFixed(2)}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography>Actual burnup to Total estimate:</Typography>
                  <Typography
                    sx={{
                      color: actualToTotalEstimateColor,
                      fontWeight: "medium",
                    }}
                  >
                    {actualToTotalEstimateRatio.toFixed(2)}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography>Actual burnup to Estimate burnup:</Typography>
                  <Typography
                    sx={{
                      color: actualToEstimateBurnupColor,
                      fontWeight: "medium",
                    }}
                  >
                    {actualToEstimateBurnupRatio.toFixed(2)}
                  </Typography>
                </Box>
              </Box>

              <Typography
                variant="h6"
                sx={{
                  borderBottom: "1px solid #e0e0e0",
                }}
              >
                Last Known Date
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Typography>Date and time stamp:</Typography>
                <Typography>{formattedLastKnownDate}</Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Container>
    </>
  );
};

export default ProjectProgress;
